import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import LoadingSpinner from '@/components/LoadingSpinner';
import MobileAppPopup from '@/components/MobileAppPopup';
import LoginPage from '@/pages/LoginPage';
import VerifyOtpPage from '@/pages/VerifyOtpPage';
import FAQPage from '@/pages/FAQPage';
import HomePage from '@/pages/HomePage';
import CookbooksPage from '@/pages/CookbooksPage';
import CookbookDetailPage from '@/pages/CookbookDetailPage';
import CookbookViewerPage from '@/pages/CookbookViewerPage';
import RecipesPage from '@/pages/RecipesPage';
import RecipeDetailPage from '@/pages/RecipeDetailPage';
import MealPlanPage from '@/pages/MealPlanPage';
import CaloriesPage from '@/pages/CaloriesPage';
import HealthInfoPage from '@/pages/HealthInfoPage';
import HealthQuizPage from '@/pages/HealthQuizPage';
import ProfilePage from '@/pages/ProfilePage';
import SpecialOffersPage from '@/pages/SpecialOffersPage';
import LegalPage from '@/pages/LegalPage';
import ManagePage from '@/pages/ManagePage';
import AdminPage from '@/pages/AdminPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import { isSupabaseConfigured } from '@/lib/supabase';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, mustChangePassword } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function ChangePasswordRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function ConfigurationError() {
  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      margin: 0,
      background: '#f5f5f5',
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        maxWidth: '500px',
      }}>
        <h1 style={{ color: '#0b5421', marginBottom: '16px' }}>Peapil Health</h1>
        <h2 style={{ color: '#333', marginBottom: '16px' }}>Configuration Required</h2>
        <p style={{ color: '#666', lineHeight: 1.6 }}>
          Please add your Supabase credentials in the Secrets tab:
        </p>
        <ul style={{ textAlign: 'left', color: '#666', lineHeight: 1.8 }}>
          <li><strong>SUPABASE_URL</strong> - Your Supabase project URL</li>
          <li><strong>SUPABASE_ANON_KEY</strong> - Your Supabase anonymous key</li>
        </ul>
        <p style={{ color: '#888', fontSize: '14px', marginTop: '16px' }}>
          After adding the secrets, refresh this page.
        </p>
      </div>
    </div>
  );
}

export default function App() {
  if (!isSupabaseConfigured) {
    return <ConfigurationError />;
  }

  return (
    <>
      <MobileAppPopup />
      <Routes>
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/verify-otp" element={<PublicRoute><VerifyOtpPage /></PublicRoute>} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/manage" element={<ManagePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/change-password" element={<ChangePasswordRoute><ChangePasswordPage /></ChangePasswordRoute>} />

        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<HomePage />} />
          <Route path="cookbooks" element={<CookbooksPage />} />
          <Route path="cookbooks/:id" element={<CookbookDetailPage />} />
          <Route path="cookbooks/:id/view" element={<CookbookViewerPage />} />
          <Route path="recipes" element={<RecipesPage />} />
          <Route path="recipes/:id" element={<RecipeDetailPage />} />
          <Route path="meal-plan" element={<MealPlanPage />} />
          <Route path="calories" element={<CaloriesPage />} />
          <Route path="health-info" element={<HealthInfoPage />} />
          <Route path="health-quiz" element={<HealthQuizPage />} />
          <Route path="special-offers" element={<SpecialOffersPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
